import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiCall } from "../api/apiCall";
import ReactSelect from "../components/common/ReactSelect";
import LoadingComponent from "../components/common/LoadingComponent";
import { Button } from "@/components/ui/button";

const AssignPermissionPage = () => {
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(new Set());
  const [clientRoles, setClientRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [allPermissions, setAllPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [isLoadingRolePermissions, setIsLoadingRolePermissions] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [modules, setModules] = useState([]);

    const hasMounted = useRef(false);
    const hasMountedRoles = useRef(false);
    const hasMountedRolesList = useRef(false);
  
  // Extract roleId from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roleId = params.get("roleId");
    if (roleId) {
      setSelectedRoleId(roleId);
    }
  }, [location]);

  // Fetch client roles
  // API response: { data: { roles: [{ role_id, role_name, ... }] } }
  useEffect(() => {
    const fetchClientRoles = async () => {
      setIsLoadingRoles(true);
      try {
        const response = await apiCall("get", `/get-all-roles?page=1&perPage=100`);

        // ✅ FIX: response.data.roles array hai, response.data nahi
        const rolesArray = response?.data?.roles;
        if (!rolesArray || !Array.isArray(rolesArray)) {
          throw new Error("Invalid roles data format.");
        }

        // ✅ FIX: role_id field use karna hai, id nahi
        const uniqueRoles = rolesArray.map((role) => ({
          id: role.role_id,
          name: role.role_name,
        }));

        setClientRoles(uniqueRoles);
      } catch (error) {
        setClientRoles([]);
        setError(error.message || "Failed to fetch roles.");
        console.error("Failed to fetch roles:", error.message);
      } finally {
        setIsLoadingRoles(false);
      }
    };
    if (hasMountedRoles.current) return;
    hasMountedRoles.current = true;
    fetchClientRoles();
  }, []);

  // Validate selectedRoleId
  useEffect(() => {
    if (selectedRoleId && clientRoles.length > 0) {
      const roleExists = clientRoles.find(
        (role) => role.id.toString() === selectedRoleId.toString()
      );
      if (!roleExists) {
        setError(`Role with ID ${selectedRoleId} not found.`);
        setSelectedRoleId("");
      }
    }
  }, [clientRoles, selectedRoleId]);

  // Fetch all permissions (only once)
  // API response: { data: [{ permission_id, permission_key, module_name, ... }] }
  useEffect(() => {
    const fetchAllPermissions = async () => {
      setIsLoadingPermissions(true);
      try {
        const permResponse = await apiCall("get", "/permissions/get-list");

        if (!permResponse?.data || !Array.isArray(permResponse.data)) {
          throw new Error("Invalid permissions data format.");
        }

        // ✅ FIX: permission_id exist karta hai, id nahi — filter fix kiya
        const permissions = permResponse.data.filter(
          (perm) => perm.permission_id && perm.permission_key && perm.module_name
        );

        setAllPermissions(permissions);

        // Organize permissions by module
        const moduleMap = {};
        permissions.forEach((perm) => {
          const moduleName = perm.module_name || "General";
          if (!moduleMap[moduleName]) {
            moduleMap[moduleName] = {
              name: moduleName,
              permissions: [],
            };
          }
          moduleMap[moduleName].permissions.push({
            permission_key: perm.permission_key,
            permission_id: perm.permission_id, // ✅ directly use karo
            description: perm.description,
            is_active: perm.is_active,
          });
        });

        setModules(Object.values(moduleMap));
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
        setError(error.message || "Failed to fetch permissions.");
        setAllPermissions([]);
        setModules([]);
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    if (hasMountedRolesList.current) return;
    hasMountedRolesList.current = true;
    fetchAllPermissions();
  }, []);

  // Fetch role permissions when role is selected
  // API response: { data: [{ permission_id, is_allowed, ... }] }
  useEffect(() => {
    let isMounted = true;

    const fetchRolePermissions = async () => {
      if (!selectedRoleId) {
        setRolePermissions([]);
        setSelectedPermissionIds(new Set());
        return;
      }

      setIsLoadingRolePermissions(true);

      try {
        const rolePermResponse = await apiCall(
          "get",
          `/permissions/role/list?role_id=${selectedRoleId}`
        );

        if (!isMounted) return;

        if (!rolePermResponse?.data) {
          throw new Error("Invalid role permissions data format.");
        }

        let permissionIds = [];

        if (Array.isArray(rolePermResponse.data)) {
          // ✅ Current API structure: array of { permission_id, is_allowed, ... }
          permissionIds = rolePermResponse.data
            .filter((rp) => rp.is_allowed)
            .map((rp) => rp.permission_id);
        } else if (
          rolePermResponse.data.permission_ids &&
          Array.isArray(rolePermResponse.data.permission_ids)
        ) {
          permissionIds = rolePermResponse.data.permission_ids;
        } else if (
          rolePermResponse.data.permissions &&
          Array.isArray(rolePermResponse.data.permissions)
        ) {
          permissionIds = rolePermResponse.data.permissions.map((p) =>
            Number(p.permission_id)
          );
        }

        setRolePermissions(permissionIds);
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to fetch role permissions:", error);
        setError(error.message || "Failed to fetch role permissions.");
        setRolePermissions([]);
      } finally {
        if (isMounted) {
          setIsLoadingRolePermissions(false);
        }
      }
    };

    fetchRolePermissions();

    return () => {
      isMounted = false;
    };
  }, [selectedRoleId]);

  // Update selected permissions when role permissions change
  useEffect(() => {
    if (!selectedRoleId || !rolePermissions.length) {
      setSelectedPermissionIds(new Set());
      return;
    }

    const newSelected = new Set();
    rolePermissions.forEach((permissionId) => {
      newSelected.add(permissionId.toString());
    });

    setSelectedPermissionIds(newSelected);
  }, [rolePermissions, selectedRoleId]);

  // Handle checkbox change
  const handleCheckboxChange = (permissionId) => {
    setSelectedPermissionIds((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(permissionId)) {
        newSelected.delete(permissionId);
      } else {
        newSelected.add(permissionId);
      }
      return newSelected;
    });
  };

  // Select/deselect all permissions for a module
  const handleSelectAllPermissions = (moduleName) => {
    setSelectedPermissionIds((prev) => {
      const newSelected = new Set(prev);
      const module = modules.find((m) => m.name === moduleName);
      if (!module) return prev;

      const allSelected = module.permissions.every((perm) =>
        newSelected.has(perm.permission_id.toString())
      );

      module.permissions.forEach((perm) => {
        if (allSelected) {
          newSelected.delete(perm.permission_id.toString());
        } else {
          newSelected.add(perm.permission_id.toString());
        }
      });

      return newSelected;
    });
  };

  const handleSelectAllModules = () => {
    setSelectedPermissionIds(() => {
      const newSelected = new Set();
      modules.forEach((module) => {
        module.permissions.forEach((perm) => {
          newSelected.add(perm.permission_id.toString());
        });
      });
      return newSelected;
    });
  };

  const handleDeselectAllModules = () => {
    setSelectedPermissionIds(new Set());
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRoleId) {
      setError("Please select a valid role.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      const assignPayload = {
        role_id: parseInt(selectedRoleId),
        permission_ids: Array.from(selectedPermissionIds).map((id) => parseInt(id)),
        is_allowed: 1,
      };

      await apiCall("post", "/permissions/role/assign", assignPayload);

      setSuccessMessage("Permissions updated successfully!");
      navigate(-1);
    } catch (error) {
      console.error("API error:", error);
      setError(error.message || "Failed to update permissions.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ FIX: ReactSelect ko { label, value } object pass karna hai, string nahi
  const roleOptions = useMemo(
    () =>
      clientRoles.map((role) => ({
        label: role.name
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        value: role.id.toString(),
      })),
    [clientRoles]
  );

  // ✅ FIX: value mein pura option object dena hai
  const selectedRoleOption = useMemo(
    () => roleOptions.find((option) => option.value === selectedRoleId?.toString()) || null,
    [roleOptions, selectedRoleId]
  );

  const filteredModules = useMemo(
    () => (allPermissions.length > 0 ? modules : []),
    [allPermissions, modules]
  );

  const isModuleAllSelected = (module) => {
    if (!module.permissions.length) return false;
    return module.permissions.every((perm) =>
      selectedPermissionIds.has(perm.permission_id.toString())
    );
  };

  const isModulePartiallySelected = (module) => {
    if (!module.permissions.length) return false;
    return (
      module.permissions.some((perm) =>
        selectedPermissionIds.has(perm.permission_id.toString())
      ) && !isModuleAllSelected(module)
    );
  };

  if (isLoadingRoles) {
    return (
      <div className="container">
        <LoadingComponent />
      </div>
    );
  }

  return (
    <div className="container-lg">
      <div className="card shadow-custom-md">
        <div className="card-body">
          {/* <h2 className="h2 mb-6">Assign Permissions</h2> */}

          {successMessage && (
            <div className="alert alert-success mb-4 flex items-center justify-between">
              <span>{successMessage}</span>
              <button
                type="button"
                className="text-success hover:text-success/80 transition-colors"
                onClick={() => setSuccessMessage("")}
              >
                ✕
              </button>
            </div>
          )}
          {error && (
            <div className="alert alert-destructive mb-4 flex items-center justify-between">
              <span>{error}</span>
              <button
                type="button"
                className="text-destructive hover:text-destructive/80 transition-colors"
                onClick={() => setError("")}
              >
                ✕
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Role Selection */}
              <div className="w-full">
                <label className="text-sm font-semibold text-muted-foreground mb-2 block">
                  Select Role <span className="text-destructive">*</span>
                </label>

                {/* ✅ FIX: value mein pura option object, onChange mein selected?.value */}
                <ReactSelect
                  options={roleOptions}
                  value={selectedRoleOption}
                  onChange={(selected) => {
                    setSelectedRoleId(selected?.value || "");
                  }}
                  placeholder="Select a role"
                  isDisabled={isLoadingRoles}
                />

                {!isLoadingRoles && clientRoles.length === 0 && (
                  <span className="text-sm text-destructive mt-2 block">
                    No roles available
                  </span>
                )}
              </div>

              {/* Permissions Section */}
              <div>
                {isLoadingPermissions ? (
                  <div className="text-center py-8">
                    <LoadingComponent />
                    <p className="text-muted-foreground mt-2">Loading permissions...</p>
                  </div>
                ) : selectedRoleId && isLoadingRolePermissions ? (
                  <div className="text-center py-8">
                    <LoadingComponent />
                    <p className="text-muted-foreground mt-2">Loading role permissions...</p>
                  </div>
                ) : selectedRoleId && filteredModules.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-destructive">
                      No permissions available. Please check configuration.
                    </span>
                  </div>
                ) : selectedRoleId ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-muted-foreground">
                        Features and Permissions
                      </label>
                      <div className="flex gap-4">
                        <button
                          type="button"
                          className="text-sm text-primary hover:text-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleSelectAllModules}
                          disabled={!selectedRoleId || filteredModules.length === 0}
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          className="text-sm text-destructive hover:text-destructive/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleDeselectAllModules}
                          disabled={!selectedRoleId || filteredModules.length === 0}
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>

                    {/* Modules List */}
                    {filteredModules.map((module) => (
                      <div
                        key={module.name}
                        className="rounded-lg border border-border p-4 bg-card"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="h5 font-semibold">{module.name}</h5>
                          <input
                            type="checkbox"
                            className="h-5 w-5 rounded border-input text-primary focus:ring-ring cursor-pointer"
                            checked={isModuleAllSelected(module)}
                            ref={(input) => {
                              if (input) {
                                input.indeterminate = isModulePartiallySelected(module);
                              }
                            }}
                            onChange={() => handleSelectAllPermissions(module.name)}
                            aria-label={`Select all permissions for ${module.name}`}
                            disabled={module.permissions.length === 0}
                          />
                        </div>

                        {module.permissions.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {module.permissions.map((perm) => (
                              <div
                                key={perm.permission_id}
                                className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring cursor-pointer mt-1"
                                  checked={selectedPermissionIds.has(
                                    perm.permission_id.toString()
                                  )}
                                  onChange={() =>
                                    handleCheckboxChange(perm.permission_id.toString())
                                  }
                                  aria-label={perm.permission_key
                                    .split("_")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() + word.slice(1)
                                    )
                                    .join(" ")}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-foreground">
                                    {perm.permission_key
                                      .split("_")
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() + word.slice(1)
                                      )
                                      .join(" ")}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {perm.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-muted-foreground text-center py-4">
                            No permissions available for this module.
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Selected Count */}
                    <div className="text-sm text-muted-foreground text-center">
                      {selectedPermissionIds.size} of {allPermissions.length} permissions
                      selected
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <span className="text-muted-foreground">
                      Please select a role to view and assign permissions
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-center gap-4">
                <Button
                variant="destructive"
                type="button"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                 type="submit"
                variant="default"
                  disabled={
                    isLoadingRoles ||
                    isLoadingPermissions ||
                    isLoadingRolePermissions ||
                    isSubmitting ||
                    !selectedRoleId
                  }
                >
                  {isSubmitting ? "Updating..." : "Update Permissions"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssignPermissionPage;